import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router";

import { AlertIcon, CheckCircleIcon, CheckIcon, CopyIcon, ExternalIcon } from "./icons";

export function Card({
  title,
  description,
  children,
  className = "",
  reveal = false,
  revealDelay = 0,
}: {
  title?: string;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
  reveal?: boolean;
  revealDelay?: number;
}) {
  return (
    <section
      className={`ui-card ${className}`}
      {...(reveal
        ? {
            "data-reveal": "",
            style: { "--reveal-delay": `${revealDelay}ms` } as CSSProperties,
          }
        : {})}
    >
      {title ? (
        <header className="ui-card__header">
          <h2>{title}</h2>
          {description ? <p>{description}</p> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export function Alert({
  tone = "info",
  title,
  children,
}: {
  tone?: "info" | "warn" | "danger" | "ok";
  title?: string;
  children: ReactNode;
}) {
  const critical = tone === "danger";

  return (
    <div
      className={`ui-alert ui-alert--${tone}`}
      role={critical ? "alert" : "status"}
      aria-live={critical ? "assertive" : "polite"}
    >
      <span className="ui-alert__mark">
        {tone === "ok" ? <CheckCircleIcon size={18} /> : <AlertIcon size={18} />}
      </span>
      <div className="ui-alert__content">
        {title ? <p className="ui-alert__title">{title}</p> : null}
        <div className="wrap-anywhere whitespace-pre-line">{children}</div>
      </div>
    </div>
  );
}

export function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <div className="ui-stat">
      <p className="ui-stat__label">{label}</p>
      <p className="ui-stat__value wrap-anywhere tabular-nums">{value}</p>
      {hint ? <p className="ui-stat__hint">{hint}</p> : null}
    </div>
  );
}

export function AddressLine({ label, address, href }: { label: string; address: string; href: string }) {
  return (
    <div className="address-row">
      <span className="address-row__label">{label}</span>
      <code className="address-row__value wrap-anywhere">{address}</code>
      <div className="address-row__actions">
        <CopyAction value={address} label="Salin alamat" />
        <a href={href} target="_blank" rel="noopener noreferrer">
          Lihat kontrak <ExternalIcon size={13} />
        </a>
      </div>
    </div>
  );
}

export function CopyAction({
  value,
  label = "Salin",
  className = "",
}: {
  value: string;
  label?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  }

  return (
    <button
      type="button"
      className={`copy-action ${copied ? "copy-action--copied" : ""} ${className}`}
      onClick={() => void copy()}
      aria-label={copied ? `${label} berhasil` : label}
    >
      {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
      {copied ? "Tersalin" : label}
    </button>
  );
}

export function ReviewRow({
  label,
  children,
  hint,
  emphasis = false,
}: {
  label: string;
  children: ReactNode;
  hint?: ReactNode;
  emphasis?: boolean;
}) {
  return (
    <div className={`review-row ${emphasis ? "review-row--emphasis" : ""}`}>
      <dt>{label}</dt>
      <dd>
        <div>{children}</div>
        {hint ? <small>{hint}</small> : null}
      </dd>
    </div>
  );
}

/** One row of the ledger panels the comps use for balances. */
export function LedgerRow({
  label,
  children,
  accent = false,
}: {
  label: string;
  children: ReactNode;
  accent?: boolean;
}) {
  return (
    <div className={`ledger__row ${accent ? "ledger__row--accent" : ""}`}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * The official contract, quoted in full.
 *
 * Full address, never shortened: the whole point of the strip is that a reader
 * can compare it character by character against what their wallet is about to
 * sign, and a middle ellipsis is exactly where an impostor address hides.
 */
export function ContractBar({
  label,
  address,
  href,
}: {
  label: string;
  address: string;
  href: string;
}) {
  return (
    <section className="contract-bar">
      <div className="contract-bar__identity">
        <p>{label}</p>
        <code>{address}</code>
      </div>
      <div className="contract-bar__actions">
        <CopyAction value={address} label="Salin" />
        <a
          className="copy-action"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ExternalIcon size={14} /> Explorer
        </a>
      </div>
    </section>
  );
}

export function PageHeader({
  context,
  title,
  description,
  meta,
  tone = "default",
}: {
  context: string;
  title: string;
  description: ReactNode;
  meta?: ReactNode;
  tone?: "default" | "danger";
}) {
  return (
    <header className={`page-intro page-intro--${tone}`}>
      {/* The terminal is the only other page; a reference document that cannot
          be left is a dead end. */}
      <Link className="page-back" to="/">
        <span aria-hidden>←</span> Kembali ke migrasi
      </Link>
      <div className="page-intro__copy">
        <p className="page-intro__context">
          <span aria-hidden />
          {context}
        </p>
        <h1>{title}</h1>
        <p className="page-intro__description">{description}</p>
      </div>
      {meta ? <div className="page-intro__meta">{meta}</div> : null}
    </header>
  );
}

export function Button({
  children,
  onClick,
  disabled,
  type = "button",
  variant = "primary",
  block = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit";
  variant?: "primary" | "secondary" | "danger";
  block?: boolean;
}) {
  const styles = {
    primary: "ui-button--primary",
    secondary: "ui-button--secondary",
    danger: "ui-button--danger",
  }[variant];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`ui-button ${styles} ${block ? "ui-button--block" : ""}`}
    >
      {children}
    </button>
  );
}
